'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Pre-audit readiness sprint',
    description:
      'Organisations approaching a SOC2 or ISO 27001 audit window with gaps in their security posture. We embed rapidly, assess where you stand, and close the critical gaps before auditors arrive, without disrupting your engineering teams.',
  },
  {
    tag: '[02]',
    title: 'IA gap assessment & remediation',
    description:
      'Internal audit has identified security gaps, or you need to get ahead of them before they do. We run a structured gap assessment against your target framework, produce a clear findings register, and then do the remediation work ourselves. Not a report that lands on your desk. Actual closure of the gaps.',
  },
  {
    tag: '[03]',
    title: 'Compliance ramp-up from scratch',
    description:
      'Starting a compliance journey with no existing programme. We build the foundations fast, policies, controls, evidence collection, and technical implementation, so you are audit-ready in weeks, not months.',
  },
  {
    tag: '[04]',
    title: 'Acquisition or investor due diligence',
    description:
      'Security posture scrutinised ahead of a funding round, M&A process, or enterprise customer onboarding. We get your security story straight, documented, and defensible under examination.',
  },
  {
    tag: '[05]',
    title: 'Post-incident recovery and hardening',
    description:
      'Following a security incident or near-miss, organisations need to move fast. We assess the blast radius, close the immediate exposure, and implement the controls that prevent recurrence.',
  },
  {
    tag: '[06]',
    title: 'Framework alignment catch-up',
    description:
      'Controls exist but have drifted from framework requirements over time. We map your current state against the framework, identify what has slipped, and bring everything back into alignment, with evidence that satisfies auditors.',
  },
];

const outcomes = [
  { metric: '6–12 wks', label: 'Typical accelerator programme duration' },
  { metric: 'Day 1', label: 'Senior engineers embedded from the start' },
  { metric: '100%', label: 'Bespoke to your environment and timeline' },
  { metric: 'Zero', label: 'Generic frameworks, everything is contextual' },
];

const phases = [
  {
    phase: 'Phase 01',
    title: 'Rapid assessment',
    body: 'We spend the first week understanding your environment, your audit scope, and your current security posture. No lengthy discovery phases, we move fast and focus on what matters.',
  },
  {
    phase: 'Phase 02',
    title: 'Gap identification & prioritisation',
    body: 'A clear, prioritised view of what needs to close before audit. Not a 200-item spreadsheet, a focused list of the controls and evidence that will determine your outcome.',
  },
  {
    phase: 'Phase 03',
    title: 'Embedded remediation',
    body: 'Our engineers work inside your team to implement the fixes. Technical controls, policy documentation, evidence collection, tooling configuration, we do the work, not just advise on it.',
  },
  {
    phase: 'Phase 04',
    title: 'Audit support & handover',
    body: 'We stay through the audit window, supporting your team with auditor queries and evidence requests. After the audit, we hand over a programme your team can maintain and build on.',
  },
];

const gapAssessmentItems = [
  {
    title: 'Structured gap analysis against target framework',
    body: 'We map your current controls, policies, and evidence against the specific framework requirements, SOC2, ISO 27001, DORA, NIST CSF, or CIS Controls. Every gap is documented with severity, effort to close, and auditor impact.',
  },
  {
    title: 'Findings register with remediation ownership',
    body: 'A clear register of every finding, categorised, prioritised, and assigned. Not a raw spreadsheet dump. A working document that drives the remediation programme and tracks closure.',
  },
  {
    title: 'Technical and process remediation',
    body: 'We close the gaps ourselves. Technical controls are implemented by our engineers. Process gaps are addressed with policies and procedures that are written, reviewed, and embedded, not templated.',
  },
  {
    title: 'Evidence collection and audit trail',
    body: 'Every remediated control is evidenced in a format auditors expect. We build the audit trail as we go, so when the auditor arrives, the evidence is already organised and defensible.',
  },
];

const differentiators = [
  {
    title: 'Speed without shortcuts',
    body: 'Accelerator programmes move fast, but we do not cut corners. Every control we implement is real, not a checkbox exercise that falls apart under auditor scrutiny.',
  },
  {
    title: 'Engineers, not consultants',
    body: 'We implement. Our team writes the policies, configures the tooling, and builds the evidence collection, not a slide deck of recommendations for your team to action alone.',
  },
  {
    title: 'Built to last beyond the audit',
    body: 'The programme we build during the accelerator is designed to sustain. We do not disappear after certification, we build something your organisation can own and operate.',
  },
];

export default function AcceleratorPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '700px', height: '700px', top: '-200px', left: '-150px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '450px', height: '450px', bottom: '-50px', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Accelerator Programme</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Accelerator Programme]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Audit-ready.<br />
              <span className="text-[#80edfa]">Fast. Without the shortcuts.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              When you need to get your security posture up to speed quickly, ahead of an audit, a funding round, or an IA gap assessment, TechSonar embeds directly into your organisation and does the work. Not a readiness report. Not a list of recommendations. Actual implementation, by senior engineers, at pace.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Talk to us about your timeline</a>
              <a href="#how-it-works" className="btn-secondary">How it works</a>
            </div>
          </div>
        </div>
      </section>

      {/* Callout strip */}
      <section className="border-t border-b border-[#80edfa]/20 py-8 bg-[#80edfa]/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-12">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-[#80edfa] animate-pulse" />
              <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Programme spotlight</p>
            </div>
            <p className="text-sm text-[#aaa] leading-relaxed">
              The Accelerator Programme is TechSonar&apos;s intensive engagement model, designed for organisations that need to move fast without compromising on the quality or durability of what gets built. Covers pre-audit readiness, IA gap assessment and remediation, compliance ramp-up, and framework alignment. Typically 6–12 weeks, fully embedded, scoped to your specific situation.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(1.75rem,3.5vw,2.5rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">When to use it</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Situations the Accelerator is built for
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                The Accelerator Programme is not a one-size-fits-all engagement. It is scoped to your specific situation, timeline, and the outcome you need to achieve, whether that is passing an audit, closing IA findings, or getting a compliance programme off the ground.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IA Gap Assessment deep-dive */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">IA gap assessments</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Find the gaps.<br />Close them.
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                Internal audit findings do not fix themselves. We run structured gap assessments against your target framework and then do the remediation, so the finding is closed, evidenced, and ready for the next audit cycle. No handoff to an already-stretched internal team.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {gapAssessmentItems?.map((item, i) => (
              <div key={i} className="bg-[#0a0f14] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="w-6 h-px bg-[#80edfa] mb-6" />
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{item?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{item?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">How it works</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Four phases. One outcome.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {phases?.map((p, i) => (
              <div key={i} className="bg-[#050a0f] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-4">{p?.phase}</p>
                <div className="w-6 h-px bg-[#333] mb-6" />
                <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{p?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{p?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Why TechSonar</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Acceleration that holds up after the audit
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-px bg-[#222]">
            {differentiators?.map((d, i) => (
              <div key={i} className="flex-1 bg-[#0a0f14] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-6" />
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{d?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{d?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Audit coming up? IA findings to close? Let&apos;s talk.
            </h2>
            <p className="text-[#aaa] text-sm mt-2">Tell us where you are and when you need to be ready, we will scope the right engagement.</p>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
